/**
 * WebSocket Hook for Real-time Updates
 * Connects to backend WebSocket for live auction updates, messages, etc.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

const WS_MESSAGE_TYPES = {
  NEW_BID: 'new_bid',
  OUTBID: 'outbid',
  NEW_MESSAGE: 'new_message',
  ORDER_UPDATE: 'order_update',
  AUCTION_ENDING: 'auction_ending',
  AUCTION_ENDED: 'auction_ended',
  TIME_EXTENDED: 'time_extended',
  NEW_REPORT: 'new_report',
  TYPING: 'typing',
};

export const useWebSocket = (userId) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  // Main connection effect
  useEffect(() => {
    if (!userId) return;

    let isMounted = true;
    let reconnectTimeout = null;
    let reconnectAttempts = 0;

    const createConnection = () => {
      if (!isMounted || !userId) return;
      
      // Get WebSocket URL from backend URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      
      // Include auth token in WebSocket URL for authentication
      const token = localStorage.getItem('token');
      const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
      const wsUrl = `${wsProtocol}://${wsHost}/ws/${userId}${tokenParam}`;
      
      console.log('[WebSocket] Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'));
      
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          if (!isMounted) return;
          console.log('[WebSocket] Connected successfully');
          setIsConnected(true);
          reconnectAttempts = 0;
          
          // If no token in URL, send auth message
          if (!token) {
            const storedToken = localStorage.getItem('token');
            if (storedToken && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'auth', token: storedToken }));
            }
          }
        };
        
        ws.onmessage = (event) => {
          if (!isMounted) return;
          try {
            const data = JSON.parse(event.data);
            console.log('[WebSocket] Message received:', data.type);
            setLastMessage(data);
          } catch (e) {
            console.error('[WebSocket] Message parse error:', e);
          }
        };
        
        ws.onclose = (event) => {
          if (!isMounted) return;
          console.log('[WebSocket] Disconnected, code:', event.code, 'reason:', event.reason);
          setIsConnected(false);
          wsRef.current = null;
          
          // Reconnect with exponential backoff
          if (reconnectAttempts < 5) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
            console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);
            reconnectTimeout = setTimeout(() => {
              reconnectAttempts++;
              createConnection();
            }, delay);
            reconnectTimeoutRef.current = reconnectTimeout;
          } else {
            console.log('[WebSocket] Max reconnection attempts reached');
          }
        };
        
        ws.onerror = (error) => {
          console.error('[WebSocket] Error:', error);
        };
        
      } catch (error) {
        console.error('[WebSocket] Connection error:', error);
      }
    };

    createConnection();

    return () => {
      isMounted = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [userId]);

  const reconnect = useCallback(() => {
    // Force reconnect by closing current connection
    if (wsRef.current) {
      wsRef.current.close();
    }
  }, []);

  return {
    isConnected,
    lastMessage,
    sendMessage,
    reconnect,
    disconnect,
    WS_MESSAGE_TYPES,
  };
};

export { WS_MESSAGE_TYPES };
export default useWebSocket;
